import React, { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Divider,
  Flex,
  Stack,
  Button,
  useToast,
  VStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Image,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import axiosInstance from '../../../services/axios';
import { useAuth } from '../../../hooks/useAuth';
import { QRCodeCanvas } from 'qrcode.react';
import MerchantSettings from './MerchantSettings';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';

/**
 * Helper function to extract error message from API response
 * Handles FastAPI validation errors which come as arrays of objects
 */
const getErrorMessage = (error) => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    // If detail is an array (FastAPI validation errors)
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    }
    // If detail is a string
    if (typeof detail === 'string') {
      return detail;
    }
    // If detail is an object
    if (typeof detail === 'object') {
      return detail.msg || JSON.stringify(detail);
    }
  }
  // Fallback to message or generic error
  return error.response?.data?.message || error.message || 'An error occurred';
};

const MerchantProfile = () => {
  const { control } = useForm();
  const toast = useToast();
  const auth = useAuth();
  const navigate = useNavigate();

  // States for Profile Update
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(auth.user?.account?.profile?.image_url || 'https://via.placeholder.com/300x300?text=Company');
  const [firstName, setFirstName] = useState(auth.user?.firstname || '');
  const [lastName, setLastName] = useState(auth.user?.lastname || '');
  const [companyName, setCompanyName] = useState(auth.user?.account?.profile?.company_name || '');

  // Other States
  const [smsConsent, setSmsConsent] = useState(auth.user?.isSMSenabled || false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  // Email/OTP
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [isEmailFormVisible, setIsEmailFormVisible] = useState(false);

  // Password
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pLoading, setPLoading] = useState(false);
  const [isPasswordFormVisible, setIsPasswordFormVisible] = useState(false);

  // Stripe - memoize to prevent re-initialization
  const stripePromise = useMemo(() =>
    loadStripe('pk_test_51PLQgX2KnKdf1YCEVojH8ER1TVelnSOzUmuNJIJaOadbq1EatVpO2RVndyWTAX0CUmp7fomagqJFkkCQpOJuJB9O00kq1zQolv'),
    []
  );

  /* =================
      HANDLERS
  ==================*/
  // Handler for updating profile image
  const handleImageSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      toast({
        title: 'No image selected.',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    setIsLoadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      await axiosInstance.post('/users/update/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({
        title: 'Company logo updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating company logo.',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingImage(false);
    }
  };

  // Handler for updating company info
  const handleInfoSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoadingInfo(true);

      await axiosInstance.put('/users/update/user/names', {
        firstname: firstName,
        lastname: lastName,
      });

      // Update company name if changed
      if (companyName !== auth.user?.account?.profile?.company_name) {
        await axiosInstance.put('/merchants/update/company-name', {
          company_name: companyName,
        });
      }

      toast({
        title: 'Company information updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating company information',
        description: getErrorMessage(error) || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingInfo(false);
    }
  };

  // Image preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  // Email change with OTP
  const sendOtp = async () => {
    setOtpLoading(true);
    try {
      const response = await axiosInstance.get('users/profile/send_otp');
      if (response.data) {
        setOtpSent(true);
        toast({
          title: 'OTP sent successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error sending OTP',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChangeEmail = async (event) => {
    event.preventDefault();
    if (!newEmail || !otp) return;
    try {
      setOtpLoading(true);
      const response = await axiosInstance.post('users/profile/update_email/', {
        new_email: newEmail,
        otp,
      });
      toast({
        title: response.data,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error changing email',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setOtpSent(false);
      setOtp('');
      setOtpLoading(false);
      setNewEmail('');
    }
  };

  // Password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast({
        title: 'Passwords do not match',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      setPLoading(true);
      await axiosInstance.post('users/profile/reset/password/', {
        old_password: oldPassword,
        new_password: newPassword,
      });
      toast({
        title: 'Password reset successful',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error resetting password',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setPLoading(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      // Log out after changing password
      auth.logout();
      navigate('/');
    }
  };

  // SMS consent
  const handleSmsConsentChange = async () => {
    try {
      const newSmsConsent = !smsConsent;
      await axiosInstance.put(`/users/update/sms/option?update=${newSmsConsent}`);
      setSmsConsent(newSmsConsent);
      toast({
        title: 'SMS consent updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating SMS consent',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // MFA (QR Code)
  const handleEnableAuthenticator = async () => {
    try {
      const response = await axiosInstance.get('/auth/qr-code');
      setQrCodeUrl(response.data.data);
      toast({
        title: 'Authenticator QR code generated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error generating QR code',
        description: getErrorMessage(error),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box py={8} px={4} bg="dark.100">
      <Flex direction="column" alignItems="center">
        <Heading color="dark.900" mb={10}>
          Settings
        </Heading>

        {/* ================================
            1) COMPANY PROFILE
        =================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Company Profile
          </Text>

          {/* Form for updating company info */}
          <Box mb={8}>
            <form onSubmit={handleInfoSubmit}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="primary.500">Company Name</FormLabel>
                  <Input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Company Name"
                    variant="filled"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="primary.500">First Name</FormLabel>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                    variant="filled"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="primary.500">Last Name</FormLabel>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                    variant="filled"
                  />
                </FormControl>

                <Button type="submit" colorScheme="teal" isLoading={isLoadingInfo}>
                  Update Company Info
                </Button>
              </Stack>
            </form>
          </Box>

          {/* Separate form for updating company logo */}
          <Box mb={8}>
            <form onSubmit={handleImageSubmit}>
              <FormControl mb={4}>
                <FormLabel color="primary.500">Company Logo</FormLabel>
                <Image
                  src={imagePreview}
                  alt="Company Logo"
                  boxSize="200px"
                  objectFit="cover"
                  mb={4}
                />
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  color="primary.900"
                  variant="unstyled"
                />
              </FormControl>
              <Button type="submit" colorScheme="teal" isLoading={isLoadingImage}>
                Update Logo
              </Button>
            </form>
          </Box>
        </Box>

        <Divider mb={8} />

        {/* ===================================
            2) CURRENT EMAIL / CHANGE EMAIL
        =====================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={2} color="dark.900">
            Email
          </Text>
          <Text mb={4} color="primary.900">
            {auth.user.email}
          </Text>
          <Button
            colorScheme="teal"
            onClick={() => setIsEmailFormVisible(!isEmailFormVisible)}
          >
            {isEmailFormVisible ? 'Cancel' : 'Change Email'}
          </Button>

          {isEmailFormVisible && (
            <Box mt={4}>
              {!otpSent ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendOtp();
                  }}
                >
                  <FormControl isRequired>
                    <FormLabel color="primary.900">New Email</FormLabel>
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="green"
                    mt={4}
                    w="200px"
                    isLoading={otpLoading}
                  >
                    Send OTP
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleChangeEmail}>
                  <FormControl isRequired>
                    <FormLabel color="primary.900">Enter OTP</FormLabel>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="green"
                    mt={4}
                    w="200px"
                    isLoading={otpLoading}
                  >
                    Change Email
                  </Button>
                </form>
              )}
            </Box>
          )}
        </Box>

        <Divider mb={8} />

        {/* =================================
            3) CHANGE PASSWORD
        ===================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Password
          </Text>
          <Button
            onClick={() => setIsPasswordFormVisible(!isPasswordFormVisible)}
          >
            {isPasswordFormVisible ? 'Cancel' : 'Change Password'}
          </Button>

          {isPasswordFormVisible && (
            <VStack spacing={4} align="flex-start" w="100%" mt={4}>
              <form onSubmit={handleResetPassword} style={{ width: '100%' }}>
                <FormControl isRequired>
                  <FormLabel color="primary.900">Old Password</FormLabel>
                  <Input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired mt={4}>
                  <FormLabel color="primary.900">New Password</FormLabel>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired mt={4}>
                  <FormLabel color="primary.900">Confirm New Password</FormLabel>
                  <Input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="green"
                  mt={4}
                  w="full"
                  isLoading={pLoading}
                >
                  Change Password
                </Button>
              </form>
            </VStack>
          )}
        </Box>

        <Divider mb={8} />

        {/* =================================================
            4) SUBSCRIPTION & PAYMENT METHODS
        ==================================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Subscription & Payment Methods
          </Text>
          <Elements stripe={stripePromise}>
            <MerchantSettings />
          </Elements>
        </Box>

        <Divider mb={8} />

        {/* Example of 'other' settings: SMS + MFA */}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Additional Security
          </Text>

          {/* SMS Consent */}
          <FormControl display="flex" alignItems="center" mb={6}>
            <FormLabel mb="0" color="primary.500">
              Opt-in for SMS Notifications?
            </FormLabel>
            <Switch
              isChecked={smsConsent}
              onChange={handleSmsConsentChange}
              colorScheme="teal"
            />
          </FormControl>

          {/* MFA / Authenticator */}
          <Button onClick={handleEnableAuthenticator} colorScheme="teal">
            View MFA QR Code
          </Button>
          {qrCodeUrl && (
            <Box mt={4}>
              <QRCodeCanvas value={qrCodeUrl} size={200} />
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default MerchantProfile;
